'use client';

/**
 * BotUnavailable - Page shown when a bot is unavailable
 * Story 5.7: Suspend Bot Accounts
 * Story 7.5: Delete or Deactivate Bot
 *
 * Displays a friendly message when a bot has been suspended,
 * deactivated, or deleted, without exposing bot content or details.
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslations } from 'next-intl';
import { BotyIcon } from '@boty/ui';

export type BotUnavailableReason = 'suspended' | 'deactivated' | 'deleted';

interface BotUnavailableProps {
  /** Reason for unavailability - defaults to 'suspended' for backward compatibility */
  reason?: BotUnavailableReason;
}

export function BotUnavailable({ reason = 'suspended' }: BotUnavailableProps) {
  const t = useTranslations('consumer.bot');

  // Use specific translations for each reason, with fallback to generic unavailable
  const titleKey = `unavailable.${reason}.title`;
  const messageKey = `unavailable.${reason}.message`;

  // Try specific translation, fall back to generic
  const title = t.has(titleKey) ? t(titleKey) : t('unavailable.title');
  const message = t.has(messageKey) ? t(messageKey) : t('unavailable.message');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        p: 4,
        textAlign: 'center',
      }}
    >
      <Box sx={{ mb: 3, opacity: 0.5 }}>
        <BotyIcon size={64} />
      </Box>

      <Typography variant="h4" component="h1" gutterBottom>
        {title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4, maxWidth: 400 }}
      >
        {message}
      </Typography>
    </Box>
  );
}

export default BotUnavailable;
